import React, { createContext, useContext, useState, useLayoutEffect, useRef } from 'react';
import logger from './utils/logger';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('signtada-theme');
    return savedTheme || 'dark';
  });
  const previousThemeRef = useRef(theme);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const isThemeChange = previousThemeRef.current !== theme;

    // Disable transitions during theme flips to avoid partial-paint flicker.
    if (isThemeChange) {
      root.classList.add('no-transition');
    }

    root.setAttribute('data-theme', theme);
    root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';
    localStorage.setItem('signtada-theme', theme);

    if (isThemeChange) {
      // Force style recalculation before re-enabling transitions.
      void root.offsetHeight;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.remove('no-transition');
        });
      });
    }

    previousThemeRef.current = theme;
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
